import { PropsWithChildren, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { CloseIcon } from '../icons/close-icon';

export const initialModalProps = { open: false, onClose: () => null, children: null, id: 'initial' };

const togglePageScroll = ({ shouldAllowScroll }: { shouldAllowScroll: boolean }): void => {
  const overflowProperty = shouldAllowScroll ? 'unset' : 'hidden';

  document.body.style.overflow = overflowProperty;

  if (document.getElementsByTagName('html').length > 0) {
    document.getElementsByTagName('html')[0].style.overflow = overflowProperty;
  }
};

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  id: string;
}

export interface ModalPropsWithSetProps extends ModalProps {
  setProps: (props: ModalProps) => void;
}

export const OriginalModal: React.FC<PropsWithChildren<ModalPropsWithSetProps>> = ({
  open,
  onClose,
  id,
  children,
  setProps,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  const openModal = useCallback((): void => {
    togglePageScroll({ shouldAllowScroll: false });
    dialogRef.current?.removeAttribute('open');
    dialogRef.current?.showModal();
  }, []);

  const closeModal = useCallback((): void => {
    togglePageScroll({ shouldAllowScroll: true });
    dialogRef.current?.close();
    setProps(initialModalProps);
    onClose();
  }, [onClose, setProps]);

  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDialogElement, MouseEvent>): void => {
      if (event.target === dialogRef.current) {
        closeModal();
      }
    },
    [closeModal],
  );

  useEffect(() => {
    if (open) {
      openModal();
    } else {
      closeModal();
    }

    return () => {
      togglePageScroll({ shouldAllowScroll: true });
    };
  }, [closeModal, open, openModal]);

  return (
    <S.Modal
      ref={dialogRef}
      data-testid={id}
      onClick={handleBackdropClick}
      onCancel={() => togglePageScroll({ shouldAllowScroll: true })}
    >
      <S.CloseContainer data-testid="close-modal" onClick={closeModal}>
        <S.CloseIcon />
      </S.CloseContainer>
      {open && <S.ChildContainer>{children}</S.ChildContainer>}
    </S.Modal>
  );
};

const S = {
  Modal: styled.dialog`
    background-color: white;
    border-radius: 6px;
    height: fit-content;
    padding: 0;
    border: none;
    overflow: visible;

    &::backdrop {
      background-color: rgba(25, 43, 53, 0.6);
    }
  `,
  ChildContainer: styled.div`
    padding: 24px;
  `,
  CloseContainer: styled.div`
    cursor: pointer;
    position: absolute;
    top: 16px;
    right: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    z-index: 1000;
  `,
  CloseIcon: styled(CloseIcon)`
    width: 16px;
    height: 16px;
    float: right;
  `,
};
